import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { useGridNavegavel } from '../../lib/useGridNavegavel';
import { api } from '../../lib/api';
import { Modal } from '../../components/Modal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { ComboBox } from '../../components/ComboBox';
import { useToast } from '../../components/Toast';
import { UpperInput } from '../../components/inputs/UpperInput';
import { PhoneInput } from '../../components/inputs/PhoneInput';
import { BuscarCnpjButton, type CnpjDados } from '../../components/inputs/BuscarCnpjButton';
import { MenuAcoesLinha, type AcaoLinha } from '../../components/MenuAcoesLinha';
import { StatusBadge } from '../../components/StatusBadge';
import { Pencil, Ban, CheckCircle2, User, Phone, MapPin, FileText, ShoppingBag, Plus, Trash2, Merge } from 'lucide-react';
import { SecaoForm } from '../../components/SecaoForm';
import { formatCpfCnpj } from '@fiodecobre/shared';
import { useAuth } from '../../lib/auth';
import { useDialog } from '../../components/DialogProvider';

// DA-311 — o fornecedor e a EMPRESA (MARTINS); o CNPJ e uma FILIAL dela (MARTINS GO, MARTINS MG).
// Quase todo fornecedor tem UM CNPJ so: a filial principal aparece inline, como sempre foi, e o
// segundo CNPJ so existe pra quem clicar em "+ outro CNPJ". Sem aba, sem wizard.

interface FilialResumo {
  id: number;
  apelido: string | null;
  cpf_cnpj: string | null;
  razao_social: string | null;
  cidade: string | null;
  uf: string | null;
  principal: boolean;
}

interface Fornecedor {
  id: number;
  tipo_pessoa: 'PF' | 'PJ';
  nome_fantasia: string | null;
  whatsapp: string | null;
  status: string;
  // Projetados da filial PRINCIPAL pela API (a lista mostra o CNPJ e a cidade dela).
  razao_social: string | null;
  cpf_cnpj: string | null;
  telefone: string | null;
  cidade: string | null;
  uf: string | null;
  filiais: FilialResumo[];
  total_filiais: number;
}

interface FormFilial {
  id?: number;
  apelido: string;
  cpf_cnpj: string; razao_social: string; inscricao_estadual: string; telefone: string;
  cep: string; endereco: string; numero: string; complemento: string;
  bairro: string; cidade: string; uf: string;
  principal: boolean;
}

interface FormFornecedor {
  tipo_pessoa: 'PF' | 'PJ';
  nome_fantasia: string;
  whatsapp: string; email: string;
  contato_nome: string; contato_telefone: string;
  coluna_codigo_produto: string;
  marca_padrao_id: number | '';
  observacoes: string;
}

const formVazio: FormFornecedor = {
  tipo_pessoa: 'PJ', nome_fantasia: '',
  whatsapp: '', email: '',
  contato_nome: '', contato_telefone: '',
  coluna_codigo_produto: '', marca_padrao_id: '', observacoes: '',
};

function filialVazia(principal = false): FormFilial {
  return {
    apelido: '', cpf_cnpj: '', razao_social: '', inscricao_estadual: '', telefone: '',
    cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '',
    principal,
  };
}

interface OpMarca { id: number; nome: string }

export function Fornecedores() {
  const toast = useToast();
  const navigate = useNavigate();
  const [itens, setItens] = useState<Fornecedor[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [editando, setEditando] = useState<Fornecedor | null>(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState<FormFornecedor>(formVazio);
  const [filiais, setFiliais] = useState<FormFilial[]>([filialVazia(true)]);
  const [confirmar, setConfirmar] = useState<Fornecedor | null>(null);
  const [marcas, setMarcas] = useState<OpMarca[]>([]);
  const { usuario } = useAuth();
  const ehAdmin = usuario?.perfil === 'admin';
  const [juntando, setJuntando] = useState<Fornecedor | null>(null);

  useEffect(() => {
    api.get<OpMarca[]>('/api/marcas').then(setMarcas).catch(() => {});
  }, []);

  async function carregar() {
    setCarregando(true);
    try {
      const lista = await api.get<Fornecedor[]>('/api/fornecedores', {
        query: { busca: busca || undefined, incluir_inativos: mostrarInativos },
      });
      setItens(lista);
    } catch (e: any) {
      toast.erro(e.message);
    } finally { setCarregando(false); }
  }
  useEffect(() => {
    const t = setTimeout(carregar, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, mostrarInativos]);

  function abrirNovo() {
    setEditando(null);
    setForm(formVazio);
    setFiliais([filialVazia(true)]);
    setModal(true);
  }

  async function abrirEditar(f: Fornecedor) {
    grid.setSelectedId(f.id); // mantem a linha destacada ao voltar do modal
    try {
      const detalhe = await api.get<any>(`/api/fornecedores/${f.id}`);
      setEditando(f);
      setForm({
        tipo_pessoa: detalhe.tipo_pessoa,
        nome_fantasia: detalhe.nome_fantasia ?? '',
        whatsapp: detalhe.whatsapp ?? '',
        email: detalhe.email ?? '',
        contato_nome: detalhe.contato_nome ?? '',
        contato_telefone: detalhe.contato_telefone ?? '',
        coluna_codigo_produto: detalhe.coluna_codigo_produto ?? '',
        marca_padrao_id: detalhe.marca_padrao_id ?? '',
        observacoes: detalhe.observacoes ?? '',
      });
      const lista: FormFilial[] = (detalhe.filiais ?? []).map((fl: any) => ({
        id: fl.id,
        apelido: fl.apelido ?? '',
        cpf_cnpj: fl.cpf_cnpj ?? '',
        razao_social: fl.razao_social ?? '',
        inscricao_estadual: fl.inscricao_estadual ?? '',
        telefone: fl.telefone ?? '',
        cep: fl.cep ?? '',
        endereco: fl.endereco ?? '',
        numero: fl.numero ?? '',
        complemento: fl.complemento ?? '',
        bairro: fl.bairro ?? '',
        cidade: fl.cidade ?? '',
        uf: fl.uf ?? '',
        principal: !!fl.principal,
      }));
      setFiliais(lista.length ? lista : [filialVazia(true)]);
      setModal(true);
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  function alterarFilial(idx: number, patch: Partial<FormFilial>) {
    setFiliais((atual) => atual.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  }

  function adicionarFilial() {
    setFiliais((atual) => [...atual, filialVazia(false)]);
  }

  function removerFilial(idx: number) {
    setFiliais((atual) => {
      const nova = atual.filter((_, i) => i !== idx);
      if (!nova.length) return [filialVazia(true)];
      // A empresa sempre tem uma principal: se a removida era ela, a primeira assume.
      if (!nova.some((f) => f.principal)) nova[0] = { ...nova[0]!, principal: true };
      return nova;
    });
  }

  function marcarPrincipal(idx: number) {
    setFiliais((atual) => atual.map((f, i) => ({ ...f, principal: i === idx })));
  }

  const temNome = !!form.nome_fantasia.trim() || filiais.some((f) => f.razao_social.trim());

  async function salvar() {
    try {
      const payload: any = {
        ...form,
        email: form.email || null,
        marca_padrao_id: form.marca_padrao_id === '' ? null : Number(form.marca_padrao_id),
        filiais: filiais.map((f, i) => ({
          ...f,
          apelido: f.apelido.trim() || (i === 0 ? 'MATRIZ' : null),
        })),
      };
      if (editando) {
        await api.put(`/api/fornecedores/${editando.id}`, payload);
        toast.sucesso('atualizado');
      } else {
        await api.post('/api/fornecedores', payload);
        toast.sucesso('criado');
      }
      setModal(false);
      void carregar();
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  async function remover() {
    if (!confirmar) return;
    try {
      await api.delete(`/api/fornecedores/${confirmar.id}`);
      toast.sucesso('inativado');
      setConfirmar(null);
      void carregar();
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  async function reativar(f: Fornecedor) {
    try {
      await api.put(`/api/fornecedores/${f.id}`, { status: 'ativo' });
      toast.sucesso('reativado');
      void carregar();
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  const sticky = useStickyHeader();
  // Padrao de grid do sistema: clicar marca a linha, ↑/↓ navegam, Enter/duplo-clique editam.
  const grid = useGridNavegavel<Fornecedor>({
    itens,
    getId: (f) => f.id,
    onAtivar: (f) => void abrirEditar(f),
  });

  const varias = filiais.length > 1;

  return (
    <div>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo">Fornecedores</h2>
          <div className="pagina-acoes">
            <input type="search" placeholder="Buscar razao, fantasia, CNPJ..." className="busca" value={busca} onChange={(e) => setBusca(e.target.value)} />
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)} />
              Mostrar inativos
            </label>
            <button type="button" className="btn-primario" onClick={abrirNovo}>+ Novo</button>
          </div>
        </div>
      </div>

      {carregando ? <p className="muted">Carregando...</p> : itens.length === 0 ? <p className="muted">Nenhum fornecedor.</p> : (
        <table className="tabela" style={sticky.tableStyle}>
          <thead><tr>
            <th style={sticky.thStyle}>Fornecedor</th>
            <th style={sticky.thStyle}>CNPJ</th>
            <th style={sticky.thStyle}>Telefone</th>
            <th style={sticky.thStyle}>Cidade</th>
            <th style={sticky.thStyle}>Status</th>
            <th style={{ ...sticky.thStyle, textAlign: 'right' }}>Acoes</th>
          </tr></thead>
          <tbody>
            {itens.map((f) => {
              const principal = f.nome_fantasia ?? f.razao_social ?? '—';
              const sub = f.nome_fantasia && f.razao_social && f.razao_social !== f.nome_fantasia ? f.razao_social : null;
              const acoes: AcaoLinha[] = [
                { label: 'Produtos comprados', icon: ShoppingBag, onClick: () => navigate(`/fornecedores/${f.id}/compras`) },
                { label: 'Editar', icon: Pencil, onClick: () => void abrirEditar(f) },
                { label: 'Juntar com outro fornecedor', icon: Merge, oculto: !ehAdmin, onClick: () => setJuntando(f) },
                { label: 'Reativar', icon: CheckCircle2, oculto: f.status === 'ativo', onClick: () => void reativar(f) },
                { label: 'Inativar', icon: Ban, perigoso: true, oculto: f.status !== 'ativo', onClick: () => setConfirmar(f) },
              ];
              const outros = (f.total_filiais ?? 0) > 1;
              return (
                <tr key={f.id} {...grid.rowProps(f)}>
                  <td>
                    <div className="cel-desc-titulo">{principal}</div>
                    {sub && <div className="cel-desc-sub">{sub}</div>}
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {formatCpfCnpj(f.cpf_cnpj)}
                      {outros && (
                        <span
                          title={(f.filiais ?? []).map((fl) => `${fl.apelido ?? ''} ${formatCpfCnpj(fl.cpf_cnpj)}`.trim()).join('\n')}
                          style={{
                            fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 999,
                            border: '1px solid var(--panel-border, #e5e7eb)',
                            color: 'var(--text-muted, #6b7280)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {f.total_filiais} CNPJs
                        </span>
                      )}
                    </span>
                  </td>
                  <td>{f.telefone ?? f.whatsapp ?? '—'}</td>
                  <td>{f.cidade ? `${f.cidade}/${f.uf ?? ''}` : '—'}</td>
                  <td><StatusBadge ativo={f.status === 'ativo'} /></td>
                  <td style={{ textAlign: 'right' }}>
                    <MenuAcoesLinha acoes={acoes} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {juntando && (
        <ModalJuntarFornecedor
          origem={juntando}
          onFechar={() => setJuntando(null)}
          onJuntou={() => { setJuntando(null); void carregar(); }}
        />
      )}

      <Modal aberto={modal} onFechar={() => setModal(false)} titulo={editando ? 'Editar fornecedor' : 'Novo fornecedor'} largura={960}
        rodape={
          <>
            {editando && (
              <button
                type="button"
                className="btn-secundario"
                onClick={() => { setModal(false); navigate(`/fornecedores/${editando.id}/compras`); }}
                title="Ver produtos ja comprados deste fornecedor (notas de entrada)"
                style={{ marginRight: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <ShoppingBag size={15} /> Produtos comprados
              </button>
            )}
            <button type="button" className="btn-secundario" onClick={() => setModal(false)}>Cancelar</button>
            <button type="button" className="btn-primario" onClick={salvar} disabled={!temNome}>Salvar</button>
          </>
        }
      >
        <div className="form-grid form-grid-12">
          <SecaoForm icone={User} titulo="Identificação" primeira />
          {/* A EMPRESA: tipo + nome fantasia. CNPJ, razao e endereco descem pra filial. */}
          <label className="col-span-12 md:col-span-3">
            Tipo
            <select value={form.tipo_pessoa} onChange={(e) => setForm({ ...form, tipo_pessoa: e.target.value as 'PF' | 'PJ' })}>
              <option value="PJ">Pessoa juridica</option>
              <option value="PF">Pessoa fisica</option>
            </select>
          </label>
          <label className="col-span-12 md:col-span-9">
            Nome fantasia
            <UpperInput value={form.nome_fantasia} onChange={(v) => setForm({ ...form, nome_fantasia: v })} autoFocus />
          </label>

          {filiais.map((fl, idx) => (
            <FilialBloco
              key={fl.id ?? `nova-${idx}`}
              filial={fl}
              indice={idx}
              varias={varias}
              tipoPessoa={form.tipo_pessoa}
              onChange={(patch) => alterarFilial(idx, patch)}
              // O nome fantasia e da EMPRESA, nao da inscricao — por isso vem por fora do `onChange`
              // da filial. So preenche quando esta VAZIO: com dois CNPJs, buscar na segunda inscricao
              // nao pode renomear a empresa, e o rotulo curto que o operador escolheu ("MARTINS" no
              // lugar de "MARTINS COMERCIO E DISTRIBUICAO SA") nao se perde a cada consulta.
              onNomeFantasia={(v) => setForm((f) => (f.nome_fantasia.trim() ? f : { ...f, nome_fantasia: v }))}
              onRemover={() => removerFilial(idx)}
              onPrincipal={() => marcarPrincipal(idx)}
            />
          ))}

          {/* Botao discreto: 3 fornecedores em 64 tem dois CNPJs — os outros 61 nao pagam por isso. */}
          <div className="col-span-12" style={{ marginTop: 6 }}>
            <button
              type="button"
              className="btn-secundario"
              onClick={adicionarFilial}
              title="Este fornecedor tem mais de uma inscricao (ex.: MARTINS GO e MARTINS MG)"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, padding: '4px 10px' }}
            >
              <Plus size={14} /> outro CNPJ
            </button>
          </div>

          <SecaoForm icone={Phone} titulo="Contato" />
          {/* Contato COMERCIAL da empresa (o telefone da inscricao fica na filial). */}
          <label className="col-span-12 md:col-span-4">
            WhatsApp
            <PhoneInput value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} />
          </label>
          <label className="col-span-12 md:col-span-8">
            Email
            <input type="email" className="no-upper" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>

          <label className="col-span-12 md:col-span-7">
            Contato (nome)
            <UpperInput value={form.contato_nome} onChange={(v) => setForm({ ...form, contato_nome: v })} />
          </label>
          <label className="col-span-12 md:col-span-5">
            Contato (telefone)
            <PhoneInput value={form.contato_telefone} onChange={(v) => setForm({ ...form, contato_telefone: v })} />
          </label>

          <SecaoForm icone={FileText} titulo="Outros" />
          <label className="col-span-12 md:col-span-6">
            Coluna codigo do produto
            <input value={form.coluna_codigo_produto} onChange={(e) => setForm({ ...form, coluna_codigo_produto: e.target.value })} />
          </label>
          <div className="col-span-12 md:col-span-6 form-grid-campo">
            <label>Marca padrao</label>
            <ComboBox
              opcoes={marcas.map((m) => ({ value: m.id, label: m.nome }))}
              value={form.marca_padrao_id}
              onChange={(v) => setForm({ ...form, marca_padrao_id: v === '' ? '' : Number(v) })}
              placeholder="Selecione (opcional)"
            />
          </div>

          <label className="col-span-12">
            Observacoes
            <textarea rows={3} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
          </label>
        </div>
      </Modal>

      <ConfirmModal aberto={!!confirmar} titulo="Inativar fornecedor" mensagem={`Inativar "${confirmar?.nome_fantasia ?? confirmar?.razao_social ?? ''}"?`} textoConfirmar="Inativar" perigoso onConfirmar={remover} onCancelar={() => setConfirmar(null)} />
    </div>
  );
}

// Um CNPJ da empresa: inscricao + endereco DELA. Com uma filial so, o bloco e o cadastro de sempre
// (nenhum enfeite de filial aparece). Com duas ou mais, ganha apelido, "principal" e remover.
function FilialBloco({ filial, indice, varias, tipoPessoa, onChange, onNomeFantasia, onRemover, onPrincipal }: {
  filial: FormFilial;
  indice: number;
  varias: boolean;
  tipoPessoa: 'PF' | 'PJ';
  onChange: (patch: Partial<FormFilial>) => void;
  onNomeFantasia: (v: string) => void;
  onRemover: () => void;
  onPrincipal: () => void;
}) {
  const titulo = varias
    ? `CNPJ ${indice + 1}${filial.apelido ? ` — ${filial.apelido}` : ''}`
    : (tipoPessoa === 'PJ' ? 'CNPJ e endereço' : 'CPF e endereço');

  return (
    <>
      <SecaoForm icone={MapPin} titulo={titulo} />

      {varias && (
        <div className="col-span-12" style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ maxWidth: 160 }}>
            Apelido
            <UpperInput value={filial.apelido} onChange={(v) => onChange({ apelido: v })} maxLength={30} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, paddingBottom: 6 }}>
            <input type="radio" checked={filial.principal} onChange={onPrincipal} />
            Principal
          </label>
          {indice > 0 && (
            <button
              type="button"
              className="btn-secundario"
              onClick={onRemover}
              title="Remover este CNPJ do fornecedor"
              style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, padding: '4px 10px' }}
            >
              <Trash2 size={14} /> Remover
            </button>
          )}
        </div>
      )}

      <label className="col-span-12 md:col-span-6">
        CNPJ/CPF
        <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
          <input
            value={filial.cpf_cnpj}
            onChange={(e) => onChange({ cpf_cnpj: e.target.value })}
            style={{ flex: 1 }}
          />
          {tipoPessoa === 'PJ' && (
            <BuscarCnpjButton
              cnpj={filial.cpf_cnpj}
              // A consulta preenche a filial em que o botao foi clicado — cada inscricao tem o
              // proprio endereco na Receita.
              onResult={(d: CnpjDados) => {
                // A Receita separa os dois nomes ("MUNDIAL REFRIGERACAO LTDA" e a razao,
                // "MUNDIAL REFRIGERACAO" e o fantasia) — antes o fantasia era descartado aqui
                // e o operador redigitava a razao no campo dele.
                if (d.nome_fantasia) onNomeFantasia(d.nome_fantasia);
                onChange({
                  razao_social: d.razao_social || filial.razao_social,
                  inscricao_estadual: d.inscricao_estadual || filial.inscricao_estadual,
                  telefone: d.telefone || filial.telefone,
                  cep: d.cep || filial.cep,
                  endereco: d.endereco || filial.endereco,
                  numero: d.numero || filial.numero,
                  complemento: d.complemento || filial.complemento,
                  bairro: d.bairro || filial.bairro,
                  cidade: d.cidade || filial.cidade,
                  uf: d.uf || filial.uf,
                });
              }}
            />
          )}
        </div>
      </label>
      <label className="col-span-12 md:col-span-3">
        Inscricao estadual
        <input value={filial.inscricao_estadual} onChange={(e) => onChange({ inscricao_estadual: e.target.value })} />
      </label>
      <label className="col-span-12 md:col-span-3">
        Telefone
        <PhoneInput value={filial.telefone} onChange={(v) => onChange({ telefone: v })} />
      </label>

      <label className="col-span-12">
        Razao social
        <UpperInput value={filial.razao_social} onChange={(v) => onChange({ razao_social: v })} />
      </label>

      <label className="col-span-4 md:col-span-2">
        CEP
        <input value={filial.cep} onChange={(e) => onChange({ cep: e.target.value })} />
      </label>
      <label className="col-span-8 md:col-span-10">
        Endereco
        <UpperInput value={filial.endereco} onChange={(v) => onChange({ endereco: v })} />
      </label>

      <label className="col-span-3 md:col-span-2">
        Numero
        <input value={filial.numero} onChange={(e) => onChange({ numero: e.target.value })} />
      </label>
      <label className="col-span-9 md:col-span-3">
        Complemento
        <UpperInput value={filial.complemento} onChange={(v) => onChange({ complemento: v })} />
      </label>
      <label className="col-span-6 md:col-span-4">
        Bairro
        <UpperInput value={filial.bairro} onChange={(v) => onChange({ bairro: v })} />
      </label>
      <label className="col-span-4 md:col-span-2">
        Cidade
        <UpperInput value={filial.cidade} onChange={(v) => onChange({ cidade: v })} />
      </label>
      <label className="col-span-2 md:col-span-1">
        UF
        <UpperInput value={filial.uf} onChange={(v) => onChange({ uf: v })} maxLength={2} />
      </label>
    </>
  );
}

interface PreviaJuntar {
  origem: { id: number; nome: string };
  destino: { id: number; nome: string };
  contagens: Array<{ chave: string; texto: string; n: number }>;
  colisoes: string[];
}

// Juntar com outro fornecedor (29/10/2026, pedido do Ruan): a empresa que entrou em dobro — em geral
// um segundo CNPJ dela, com outra razao social, criado pela importacao de nota — passa inteira pra
// dentro da principal escolhida aqui. A peca e as travas moram no backend (fornecedor-juntar.ts);
// esta janela so escolhe o principal, mostra o que muda de dono e o que impede.
function ModalJuntarFornecedor({ origem, onFechar, onJuntou }: {
  origem: Fornecedor;
  onFechar: () => void;
  onJuntou: () => void;
}) {
  const toast = useToast();
  const dialog = useDialog();
  const [todos, setTodos] = useState<Fornecedor[]>([]);
  const [destinoId, setDestinoId] = useState<number | null>(null);
  const [previa, setPrevia] = useState<PreviaJuntar | null>(null);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const nomeOrigem = origem.nome_fantasia || origem.razao_social || `Fornecedor ${origem.id}`;

  useEffect(() => {
    api.get<Fornecedor[]>('/api/fornecedores', { query: { incluir_inativos: true } })
      .then(setTodos)
      .catch((e: any) => setErro(e.message));
  }, []);

  useEffect(() => {
    setPrevia(null);
    setErro(null);
    if (destinoId == null) return;
    let vivo = true;
    setCarregandoPrevia(true);
    api.get<PreviaJuntar>(`/api/fornecedores/${origem.id}/juntar-previa`, { query: { destino: destinoId } })
      .then((p) => { if (vivo) setPrevia(p); })
      .catch((e: any) => { if (vivo) setErro(e.message); })
      .finally(() => { if (vivo) setCarregandoPrevia(false); });
    return () => { vivo = false; };
  }, [destinoId, origem.id]);

  const opcoes = todos
    .filter((f) => f.id !== origem.id)
    .map((f) => ({
      value: f.id,
      label: `${f.nome_fantasia || f.razao_social || `Fornecedor ${f.id}`}${f.cpf_cnpj ? ` — ${formatCpfCnpj(f.cpf_cnpj)}` : ''}${f.status !== 'ativo' ? ' (inativo)' : ''}`,
    }));

  const motivoBloqueio = destinoId == null
    ? 'Escolha o fornecedor principal.'
    : carregandoPrevia || !previa
      ? 'Carregando a previa...'
      : previa.colisoes.length > 0
        ? 'Resolva os registros em dobro listados acima antes de juntar.'
        : null;

  async function juntar() {
    if (!previa || motivoBloqueio) return;
    const ok = await dialog.confirmar(
      `${previa.origem.nome} deixa de existir e tudo dele passa para ${previa.destino.nome}. Nao tem volta. Confirma?`,
      { perigoso: true },
    );
    if (!ok) return;
    setGravando(true);
    try {
      await api.post(`/api/fornecedores/${origem.id}/juntar`, { destino_id: previa.destino.id });
      toast.sucesso(`Juntado em ${previa.destino.nome}`);
      onJuntou();
    } catch (e: any) {
      setErro(e.message);
    } finally {
      setGravando(false);
    }
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Juntar com outro fornecedor"
      largura={640}
      rodape={
        <>
          <button type="button" className="btn-secundario" onClick={onFechar} disabled={gravando}>Cancelar</button>
          <button type="button" className="btn-primario" onClick={() => void juntar()} disabled={!!motivoBloqueio || gravando}>
            {gravando ? 'Juntando...' : 'Juntar'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ margin: 0 }}>
          <strong>{nomeOrigem}</strong>
          {origem.cpf_cnpj ? ` (${formatCpfCnpj(origem.cpf_cnpj)})` : ''} vai deixar de existir. O CNPJ, o catalogo,
          as notas e as contas a pagar dele passam para o fornecedor principal escolhido abaixo.
        </p>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          Fornecedor principal (o que fica)
          <ComboBox
            opcoes={opcoes}
            value={destinoId}
            onChange={(v) => setDestinoId(v === '' ? null : Number(v))}
            placeholder="Escolha o fornecedor principal..."
          />
        </label>

        {/* Recusa, erro e motivo vem ANTES da lista: com a janela baixa a lista empurra o resto pra
            baixo da dobra, e o "Juntar" apagado ficaria mudo. */}
        {previa && previa.colisoes.length > 0 && (
          <div style={{ background: '#f97316', color: '#000', borderRadius: 6, padding: '10px 12px' }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Nao da pra juntar — estes registros ficariam em dobro:</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {previa.colisoes.map((c) => <li key={c}>{c}</li>)}
            </ul>
          </div>
        )}

        {erro && (
          <div style={{ background: '#ef4444', color: '#000', borderRadius: 6, padding: '10px 12px', fontWeight: 600 }}>{erro}</div>
        )}

        {motivoBloqueio && !erro && !(previa && previa.colisoes.length > 0) && (
          <div className="muted" style={{ fontSize: 13 }}>{motivoBloqueio}</div>
        )}

        {previa && (
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Passa para {previa.destino.nome}:</div>
            {previa.contagens.length === 0 ? (
              <div className="muted">Nada alem do cadastro — ele nao tem nenhum registro ligado.</div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {previa.contagens.map((c) => (
                  <li key={c.chave}>{c.texto}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
